import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, Camera, Send, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarRating = ({ rating, onRate, interactive = false, size = 16 }) => (
  <div className="flex gap-0.5" data-testid="star-rating">
    {[1, 2, 3, 4, 5].map(i => (
      <button
        key={i}
        type="button"
        disabled={!interactive}
        onClick={() => interactive && onRate(i)}
        className={interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}
        data-testid={`star-${i}`}
      >
        <Star
          size={size}
          className={i <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
        />
      </button>
    ))}
  </div>
);

const ReviewSection = ({ artworkId, artworkTitle }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [photo, setPhoto] = useState(null);

  const fetchReviews = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reviews/artwork/${artworkId}`);
      setReviews(res.data.reviews || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [artworkId]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reviewText.trim() || reviewText.trim().length < 10) {
      toast.error('Review must be at least 10 characters');
      return;
    }
    setSubmitting(true);
    const fd = new FormData();
    fd.append('artwork_id', artworkId);
    fd.append('rating', rating);
    fd.append('review_text', reviewText.trim());
    if (photo) fd.append('photo', photo);

    try {
      await axios.post(`${API}/reviews`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSubmitted(true);
      setReviewText('');
      setPhoto(null);
      setRating(5);
      toast.success('Review submitted! It will appear after admin approval.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const avgRating = reviews.length > 0
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  return (
    <div className="mt-6 border-t border-border pt-6" data-testid="review-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif text-lg font-light">
          Customer Reviews
          {reviews.length > 0 && (
            <span className="ml-2 text-sm text-muted-foreground font-sans">
              ({reviews.length}) {avgRating && `· ${avgRating}`} <Star size={12} className="inline fill-amber-400 text-amber-400 -mt-0.5" />
            </span>
          )}
        </h3>
        {user && !showForm && !submitted && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowForm(true)}
            data-testid="write-review-btn"
          >
            <Star size={14} className="mr-1.5" /> Write a Review
          </Button>
        )}
      </div>

      {/* Submit form */}
      {showForm && !submitted && (
        <form onSubmit={handleSubmit} className="bg-muted/30 rounded-lg p-4 mb-5 border border-border" data-testid="review-form">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-xs text-muted-foreground font-sans">Your Rating:</span>
            <StarRating rating={rating} onRate={setRating} interactive size={20} />
          </div>
          <Textarea
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
            placeholder="Share your experience with this artwork..."
            rows={3}
            className="mb-3"
            data-testid="review-text-input"
          />
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer text-xs text-muted-foreground hover:text-foreground transition-colors">
              <Camera size={14} />
              <span>{photo ? photo.name : 'Add a photo (optional)'}</span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => setPhoto(e.target.files[0] || null)}
                data-testid="review-photo-input"
              />
            </label>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={submitting || !reviewText.trim()}
                className="bg-accent hover:bg-accent/90"
                data-testid="submit-review-btn"
              >
                {submitting ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Send size={14} className="mr-1.5" />}
                Submit
              </Button>
            </div>
          </div>
        </form>
      )}

      {submitted && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-5 flex items-center gap-3" data-testid="review-submitted">
          <CheckCircle size={18} className="text-green-600 shrink-0" />
          <p className="text-sm text-green-800">Your review has been submitted and will appear after admin approval.</p>
        </div>
      )}

      {/* Reviews list */}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
          <Loader2 size={14} className="animate-spin" /> Loading reviews...
        </div>
      ) : reviews.length > 0 ? (
        <div className="space-y-4" data-testid="reviews-list">
          {reviews.map(review => (
            <div key={review.id} className="bg-card rounded-lg p-4 border border-border" data-testid={`review-${review.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-accent/10 flex items-center justify-center">
                    <span className="text-xs font-semibold text-accent">{(review.customer_name || 'A')[0].toUpperCase()}</span>
                  </div>
                  <div>
                    <p className="font-sans text-sm font-medium">{review.customer_name}</p>
                    <p className="font-sans text-[10px] text-muted-foreground">
                      {new Date(review.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                </div>
                <StarRating rating={review.rating} size={13} />
              </div>
              <p className="font-sans text-sm text-muted-foreground leading-relaxed">{review.review_text}</p>
              {review.photo_url && (
                <img
                  src={`${API.replace('/api', '')}${review.photo_url}`}
                  alt="Review photo"
                  className="mt-3 rounded-lg max-h-48 object-cover border border-border"
                  data-testid={`review-photo-${review.id}`}
                />
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="font-sans text-sm text-muted-foreground py-2">
          No reviews yet.{user ? ' Be the first to share your experience!' : ' Sign in to leave a review.'}
        </p>
      )}
    </div>
  );
};

export default ReviewSection;
